<!-- One section of Profile → Settings' switches (2026-10-20, Seán's Design 1): a bold capitals
     heading, its description beside it (or the "?" in the slot), then the rows in one light card,
     a thin line between them. A click anywhere on a row toggles its switch; the switch itself
     takes the keyboard (Space, Enter) and tells screen readers its state. -->
<template>
  <section class="settingsSection" :aria-labelledby="title ? headingId : undefined">
    <!-- no title: no heading (the mailing-list dialog: its own title says what they are) -->
    <h3 v-if="title" :id="headingId" class="heading">
      <span class="title">{{ title }}</span>
      <span v-if="description" class="description">{{ description }}</span>
      <slot name="help" />
    </h3>
    <div class="card" :class="{isSaving}">
      <div
        v-for="row in rows"
        :key="row.key"
        class="row"
        :class="{isOff: !row.value}"
        :data-setting="row.key"
        @click="(event) => toggleFromRow(event, row)">
        <span class="label">{{ row.label }}</span>
        <!-- row.isPending: its change is still being saved (the Subscriptions dialog) -->
        <AppSpinner v-if="row.isPending" class="pending" />
        <AppSwitch
          :model-value="row.value"
          :label="row.label"
          @update:modelValue="(value) => emit('toggle', row.key, value)" />
      </div>
    </div>
  </section>
</template>

<script setup>
import {computed} from "vue";
import AppSpinner from "@/components/App/Spinner";
import AppSwitch from "@/components/App/Switch";

// rows: [{key, label, value, isPending}]
const props = defineProps({
  title: {type: String, default: ""},
  description: {type: String, default: ""},
  rows: {type: Array, default: () => []},
  isSaving: {type: Boolean, default: false},
});
const emit = defineEmits(["toggle"]);
const headingId = computed(() => `settings-${props.title.toLowerCase()}`);

// a click on the switch is the switch's own; anywhere else on the row, the same change
function toggleFromRow(event, row) {
  if (props.isSaving || event.target.closest("[role='switch']")) return;
  emit("toggle", row.key, !row.value);
}
</script>

<style scoped>
/* the gap above each section: --section-gap, set by the dialog (index.vue) */
.settingsSection {
  margin-top: var(--section-gap, 12px);
}
.heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin: 0 0 5px;
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
}
.title {
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #1a1a1a;
}
.description {
  font-size: 11.5px;
  color: rgba(0, 0, 0, 0.55);
}
.card {
  background: #fcfcfc;
  border: 1px solid #e3e3e3;
  border-radius: 12px;
}
.row {
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  min-height: 30px; /* lower than 40px (Seán, 2026-10-20): Settings fits a phone */
  padding: 0 10px 0 12px;
  cursor: pointer;
}
/* the thin line between rows, inset at both ends as in the design */
.row + .row::before {
  content: "";
  position: absolute;
  top: 0;
  left: 10px;
  right: 10px;
  border-top: 1px solid #e6e6e6;
}
.label {
  grid-column: 1;
  font-size: 14px;
  color: #222;
  transition: color 0.2s;
}
.pending {
  grid-column: 2;
  margin-right: 8px;
  font-size: 14px;
  color: #8a8a8a;
}
.row :deep([role="switch"]) {
  grid-column: 3;
}
/* switched off: the words muted, the switch as it is (still clearly changeable) */
.isOff .label {
  color: rgba(0, 0, 0, 0.55); /* ~#737373 on the card: 4.7:1, AA */
}
.isSaving {
  pointer-events: none;
  opacity: 0.6;
}
/* a small phone with its browser bars (an iPhone SE in Safari: 553px): tighter still */
@media (max-height: 600px) {
  .heading {
    margin-bottom: 3px;
  }
  .row {
    min-height: 28px;
  }
}
/* the whole row toggles its switch: the switch a little darker on hover (DESIGN.md § 15) */
.card:not(.isSaving) .row:hover :deep([role="switch"]) {
  filter: brightness(0.93);
}
</style>
